import type { Dayjs } from 'dayjs';
import {
  toIsoDate,
  type ClientAddress,
  type ClientContact,
  type CompanyLookupAddress,
  type CompanyLookupResult,
  type ClientDetail,
  type SaveClientPayload,
} from '@/core/models/practice.models';
import { COMPANIES_HOUSE_NAME_MAX } from '@/core/utils/companiesHouseLimits';
import {
  EMAIL_PATTERN,
  PHONE_PATTERN,
  maxLength,
  pattern,
  required,
  requiredText,
  runValidators,
  type AddressLine,
  type FieldErrors,
  type Validator,
} from '../formRules';

/**
 * APT380: a company (type 0) or LLP (type 3) is registered at Companies House, whose limit for a name is 160 characters including
 * spaces; a sole trader, partnership or individual has no such limit and keeps the 255 the record has always allowed.
 */
export const companyNameMaxFor = (clientType: string): number =>
  clientType === '0' || clientType === '3' ? COMPANIES_HOUSE_NAME_MAX : 255;

// ap5-a3: UK reference formats, applied as input validation with explicit messages.
export const REGISTRATION_NUMBER_PATTERN = /^(?:[A-Za-z]{2}\d{6}|\d{8})$/;
export const VAT_REG_NO_PATTERN = /^\d{9}$/;
export const PAYE_REF_PATTERN = /^\d{3}\/[A-Za-z0-9]{1,10}$/;
export const ACCOUNTS_OFFICE_REF_PATTERN = /^\d{3}P[A-Za-z]\d{8}$/;
export const UTR_PATTERN = /^\d{10}$/;
export const NINO_PATTERN = /^[A-Za-z]{2}\d{6}[A-Da-d]$/;

export const REGISTRATION_NUMBER_HINT =
  'The Companies House registration number: 8 characters, either 8 digits or 2 letters followed by 6 digits (e.g. 01234567 or SC123456).';

export type DateValue = Dayjs | string | null;

/** The reactive `clientForm`. Null is what `FormGroup.reset()` leaves in a control that was not given a value. */
export interface ClientFormValues {
  clientId: string | null;
  /** A string ('0' Limited Company, '1' Sole Trader, '2' Partnership, '3' LLP), as the radio group holds it. */
  clientType: string;
  companyName: string | null;
  companyType: string | null;
  nationalInsuranceNumber: string | null;
  isCommunityInterest: boolean;
  businessStartDate: DateValue;
  bookStartDate: DateValue;
  yearEnd: DateValue;
  registrationNumber: string | null;
  utrNo: string | null;
  vatScheme: string | null;
  vatRegNo: string | null;
  vatRegistrationDate: DateValue;
  vatSubmitType: string | null;
  payeRefNo: string | null;
  accountsOfficeRefNo: string | null;
}

export type ClientControlName = keyof ClientFormValues;

export interface ContactLine {
  contactId: string | null;
  title: string;
  fullName: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

export interface ClientAddressLine extends AddressLine {
  isPrimary: boolean;
}

export interface TypeFlags {
  isTrader: boolean;
  /** ap5-a3: only a limited company has a company type / CIC status. */
  showCompanyFields: boolean;
  /** ap5-a3: companies and LLPs are registered; sole traders and partnerships are not. */
  showRegistrationNumber: boolean;
  companyNameLabel: string;
}

/** The flags `applyClientTypeRules(value)` sets. */
export function typeFlags(clientType: string): TypeFlags {
  const showCompanyFields = clientType === '0';
  const isIndividual = clientType === 'individual' || clientType === '4';
  const isTrader = clientType === '1' || isIndividual;
  return {
    isTrader,
    showCompanyFields,
    showRegistrationNumber: clientType === '0' || clientType === '3',
    companyNameLabel: showCompanyFields ? 'Name of Company' : 'Business Name',
  };
}

/**
 * `applyClientTypeRules(value)`: the values it forces. NI is cleared unless a sole trader; company type and CIC are
 * cleared unless a limited company; the registration number is cleared unless a company or LLP. (The validators it
 * swaps are derived from the type in `validatorsFor`.)
 */
export function applyClientTypeRules(values: ClientFormValues, clientType: string): ClientFormValues {
  const flags = typeFlags(clientType);
  const next = { ...values, clientType };
  if (!flags.isTrader) {
    next.nationalInsuranceNumber = '';
  }
  if (!flags.showCompanyFields) {
    next.companyType = '';
    next.isCommunityInterest = false;
  }
  if (!flags.showRegistrationNumber) {
    next.registrationNumber = '';
  }
  return next;
}

/** `clientForm.reset({ clientType: '0', isCommunityInterest: false })` followed by `applyClientTypeRules('0')`. */
export function newClientValues(): ClientFormValues {
  return applyClientTypeRules(
    {
      clientId: null,
      clientType: '0',
      companyName: null,
      companyType: null,
      nationalInsuranceNumber: null,
      isCommunityInterest: false,
      businessStartDate: null,
      bookStartDate: null,
      yearEnd: null,
      registrationNumber: null,
      utrNo: null,
      vatScheme: null,
      vatRegNo: null,
      vatRegistrationDate: null,
      vatSubmitType: null,
      payeRefNo: null,
      accountsOfficeRefNo: null,
    },
    '0',
  );
}

/** `populate(client)`: the form reset with the saved client, then `applyClientTypeRules`. */
export function clientValuesFrom(client: ClientDetail): ClientFormValues {
  return applyClientTypeRules(
    {
      clientId: client.clientId,
      clientType: String(client.clientType),
      companyName: client.companyName,
      companyType: client.companyType ?? '',
      nationalInsuranceNumber: client.nationalInsuranceNumber ?? '',
      isCommunityInterest: client.isCommunityInterest,
      businessStartDate: client.businessStartDate,
      bookStartDate: client.bookStartDate,
      yearEnd: client.yearEnd,
      registrationNumber: client.registrationNumber ?? '',
      utrNo: client.utrNo ?? '',
      vatScheme: client.vatScheme ?? '',
      vatRegNo: client.vatRegNo ?? '',
      vatRegistrationDate: client.vatRegistrationDate,
      vatSubmitType: client.vatSubmitType ?? '',
      payeRefNo: client.payeRefNo ?? '',
      accountsOfficeRefNo: client.accountsOfficeRefNo ?? '',
    },
    String(client.clientType),
  );
}

/** The validators each control carries: the constructor's, as `applyClientTypeRules` swaps them per client type. */
export function validatorsFor(name: ClientControlName, clientType: string): Validator[] {
  const flags = typeFlags(clientType);
  switch (name) {
    case 'clientId':
      return [requiredText];
    case 'companyName':
      return [requiredText, maxLength(companyNameMaxFor(clientType))];
    case 'companyType':
      return [];
    case 'nationalInsuranceNumber':
      return clientType === '1'
        ? [required, pattern(NINO_PATTERN)]
        : clientType === 'individual' || clientType === '4'
          ? [pattern(NINO_PATTERN)]
          : [];
    case 'registrationNumber':
      return flags.showRegistrationNumber ? [pattern(REGISTRATION_NUMBER_PATTERN)] : [];
    case 'utrNo':
      return [pattern(UTR_PATTERN)];
    case 'vatRegNo':
      return [pattern(VAT_REG_NO_PATTERN)];
    case 'payeRefNo':
      return [pattern(PAYE_REF_PATTERN)];
    case 'accountsOfficeRefNo':
      return [pattern(ACCOUNTS_OFFICE_REF_PATTERN)];
    default:
      return [];
  }
}

export function controlErrors(name: ClientControlName, values: ClientFormValues): FieldErrors | null {
  return runValidators(values[name], validatorsFor(name, values.clientType));
}

/** `dateOrderValidator` (group validator): book start and VAT registration cannot precede the business start. */
export function dateOrderErrors(
  values: ClientFormValues,
): { bookBeforeBusiness?: true; vatBeforeBusiness?: true } | null {
  const businessStart = toIsoDate(values.businessStartDate);
  if (!businessStart) {
    return null;
  }

  const errors: { bookBeforeBusiness?: true; vatBeforeBusiness?: true } = {};
  const bookStart = toIsoDate(values.bookStartDate);
  if (bookStart && bookStart < businessStart) {
    errors.bookBeforeBusiness = true;
  }

  const vatRegistration = toIsoDate(values.vatRegistrationDate);
  if (vatRegistration && vatRegistration < businessStart) {
    errors.vatBeforeBusiness = true;
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

export const CONTROL_NAMES: ClientControlName[] = [
  'clientId',
  'clientType',
  'companyName',
  'companyType',
  'nationalInsuranceNumber',
  'isCommunityInterest',
  'businessStartDate',
  'bookStartDate',
  'yearEnd',
  'registrationNumber',
  'utrNo',
  'vatScheme',
  'vatRegNo',
  'vatRegistrationDate',
  'vatSubmitType',
  'payeRefNo',
  'accountsOfficeRefNo',
];

/** `clientForm.valid`: no control errors and no group error. */
export function isClientFormValid(values: ClientFormValues): boolean {
  return CONTROL_NAMES.every((name) => controlErrors(name, values) === null) && dateOrderErrors(values) === null;
}

/**
 * `errorFor(controlName)`: the field-level message, only once the control is dirty or touched.
 * Order: required, maxlength, pattern (per control), anything else.
 */
export function errorFor(name: ClientControlName, values: ClientFormValues, dirtyOrTouched: boolean): string | null {
  const errors = controlErrors(name, values);
  if (!errors || !dirtyOrTouched) {
    return null;
  }
  if (errors.required) {
    return 'This field is required.';
  }
  if (errors.maxlength) {
    return `Maximum ${errors.maxlength.requiredLength} characters.`;
  }
  if (errors.pattern) {
    switch (name) {
      case 'registrationNumber':
        return 'Registration number must be 8 characters: 8 digits, or 2 letters followed by 6 digits.';
      case 'vatRegNo':
        return 'VAT registration number must be 9 digits.';
      case 'payeRefNo':
        return 'PAYE reference must look like 123/AB12345.';
      case 'accountsOfficeRefNo':
        return 'Accounts Office reference must look like 123PA00123456.';
      case 'utrNo':
        return 'UTR must be 10 digits.';
      case 'nationalInsuranceNumber':
        return 'National Insurance number must look like AA999999A.';
      default:
        return 'This value is not in the expected format.';
    }
  }
  return 'This value is not valid.';
}

/** ap5-a3: book start date cannot precede the business start date. */
export function bookStartDateError(values: ClientFormValues): string | null {
  return dateOrderErrors(values)?.bookBeforeBusiness
    ? 'Book start date cannot be before the business start date.'
    : null;
}

/** ap5-a3: VAT registration date cannot precede the business start date. */
export function vatRegistrationDateError(values: ClientFormValues): string | null {
  return dateOrderErrors(values)?.vatBeforeBusiness
    ? 'VAT registration date cannot be before the business start date.'
    : null;
}

export function blankAddress(isPrimary: boolean): ClientAddressLine {
  return {
    addressType: '0',
    addressLine: '',
    city: '',
    county: '',
    country: 'United Kingdom',
    postCode: '',
    isPrimary,
  };
}

export function blankContact(isPrimary: boolean): ContactLine {
  return { contactId: null, title: '', fullName: '', email: '', phone: '', isPrimary };
}

/**
 * APT241: the form once a company is chosen from Companies House (by registration number or from the name search).
 * Choosing a company used to fill only the name; now every value Companies House holds that the form has a field for
 * is filled: the client type (Limited Company or LLP), Type of Company, the CIC flag, the Business Start Date
 * (incorporation), the Book Start Date and Year End (the next accounts period). A value Companies House does not
 * give leaves the field as it was.
 */
export function applyCompanyLookup(
  values: ClientFormValues,
  company: CompanyLookupResult,
  typedNumber: string,
): ClientFormValues {
  const clientType =
    (company.clientType === 0 || company.clientType === 3) && String(company.clientType) !== values.clientType
      ? String(company.clientType)
      : values.clientType;
  const next = clientType === values.clientType ? { ...values } : applyClientTypeRules(values, clientType);

  next.companyName = company.companyName;
  next.registrationNumber = company.companyNumber || typedNumber;

  if (typeFlags(clientType).showCompanyFields) {
    if (company.limitedBy) next.companyType = company.limitedBy;
    if (typeof company.isCommunityInterest === 'boolean') next.isCommunityInterest = company.isCommunityInterest;
  }
  if (company.incorporatedOn) next.businessStartDate = company.incorporatedOn;
  if (company.nextAccountsPeriodStart) next.bookStartDate = company.nextAccountsPeriodStart;
  if (company.yearEnd) next.yearEnd = company.yearEnd;

  return next;
}

/**
 * APT241: the address grid with the company's registered office in the primary row (the first row when none is
 * primary, a new primary row when the grid is empty). The registered office replaces the whole address, so a part
 * Companies House does not give is left blank rather than mixed with the old address.
 */
export function withRegisteredOffice(
  rows: readonly ClientAddressLine[],
  office: CompanyLookupAddress | null | undefined,
): ClientAddressLine[] {
  if (!office) return [...rows];

  const address = {
    addressLine: office.addressLine ?? '',
    city: office.city ?? '',
    county: office.county ?? '',
    country: office.country || 'United Kingdom',
    postCode: office.postCode ?? '',
  };
  if (rows.length === 0) return [{ ...blankAddress(true), ...address }];

  const primary = rows.findIndex((row) => row.isPrimary);
  const target = primary >= 0 ? primary : 0;
  return rows.map((row, i) => (i === target ? { ...row, ...address } : row));
}

/** `removeAddress` / `removeContact`: drop the row; the first remaining row becomes primary if none is; never empty. */
export function removeRow<T extends { isPrimary: boolean }>(rows: readonly T[], index: number, blank: () => T): T[] {
  const kept = rows.filter((_, i) => i !== index);
  if (kept.length > 0 && !kept.some((row) => row.isPrimary)) {
    kept[0] = { ...kept[0], isPrimary: true };
  }
  return kept.length > 0 ? kept : [blank()];
}

/** `setPrimaryAddress` / `setPrimaryContact`: exactly the chosen row is primary. */
export function setPrimary<T extends { isPrimary: boolean }>(rows: readonly T[], selected: number): T[] {
  return rows.map((row, i) => ({ ...row, isPrimary: i === selected }));
}

/** ap5-a3: email and phone validation for the contact grid. */
export function validateContacts(rows: readonly ContactLine[]): string | null {
  for (const row of rows) {
    const hasAny = !!(row.fullName || row.email || row.phone);
    if (!hasAny) {
      continue;
    }
    if (!row.fullName?.trim()) {
      return 'Each contact needs a full name.';
    }
    if (row.email && !EMAIL_PATTERN.test(row.email.trim())) {
      return `'${row.email}' is not a valid email address.`;
    }
    if (row.phone && !PHONE_PATTERN.test(row.phone.trim())) {
      return `'${row.phone}' is not a valid phone number.`;
    }
  }
  return null;
}

/** The `SaveClientPayload` `save()` builds from the form and the two grids. */
export function buildClientPayload(
  id: string | null,
  value: ClientFormValues,
  addresses: readonly ClientAddressLine[],
  contacts: readonly ContactLine[],
): SaveClientPayload {
  const flags = typeFlags(value.clientType);
  const isCompany = flags.showCompanyFields;
  return {
    id,
    clientId: value.clientId as string,
    clientType: value.clientType === 'individual' || value.clientType === '4' ? 1 : Number(value.clientType),
    companyName: value.companyName as string,
    companyType: isCompany ? value.companyType || null : null,
    nationalInsuranceNumber: value.nationalInsuranceNumber || null,
    isCommunityInterest: isCompany ? !!value.isCommunityInterest : false,
    businessStartDate: toIsoDate(value.businessStartDate),
    bookStartDate: toIsoDate(value.bookStartDate),
    yearEnd: toIsoDate(value.yearEnd),
    registrationNumber: flags.showRegistrationNumber ? value.registrationNumber || null : null,
    utrNo: value.utrNo || null,
    vatScheme: value.vatScheme || null,
    vatRegNo: value.vatRegNo || null,
    vatRegistrationDate: toIsoDate(value.vatRegistrationDate),
    vatSubmitType: value.vatSubmitType || null,
    vatSubmitTypeValue: null,
    payeRefNo: value.payeRefNo || null,
    accountsOfficeRefNo: value.accountsOfficeRefNo || null,
    addresses: addresses.map<ClientAddress>((row) => ({
      addressType: Number(row.addressType ?? 0),
      addressLine: row.addressLine,
      city: row.city,
      county: row.county || null,
      country: row.country || 'United Kingdom',
      postCode: row.postCode,
      isPrimary: row.isPrimary,
    })),
    contacts: contacts.map<ClientContact>((row) => ({
      contactId: row.contactId,
      title: row.title || null,
      fullName: row.fullName,
      email: row.email || null,
      phone: row.phone || null,
      isPrimary: row.isPrimary,
    })),
  };
}
